import { Alert, Badge, Button, Group, Loader, Progress, Stack, Text, Title } from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { useGetApiUsers } from '../../codegen/generated/users'

import { LXTable, LXTableColumn } from '../../components/LXTable/LXTable'
import { DateHelper, LONG_FORMAT } from '../../helpers/Date.helper'
import { NumberFormatter } from '../../helpers/NumberFormatter.helper'
import { FileCopyService } from '../../services/FileCopy.service'
import { FileCopyOperation, FileCopyOperationStatus } from '../../types/FileCopy'

type Props = {
  onGoToCreate: () => void
  refreshKey: number
}

const STATUS_COLORS: Record<FileCopyOperationStatus, string> = {
  pending: 'gray',
  running: 'blue',
  completed: 'green',
  failed: 'red',
  cancelled: 'gray',
}

type FileCopyOperationRow = {
  id: string
  operation: JSX.Element
  createdBy: JSX.Element
  status: FileCopyOperationStatus
  statusItem: JSX.Element
  selectionAndDestination: JSX.Element
  progress: JSX.Element
  files: JSX.Element
}

export const FileCopyOperationsList = ({ onGoToCreate, refreshKey }: Props): JSX.Element => {
  const t = useTranslate()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [statuses, setStatuses] = useState<FileCopyOperationStatus[]>([])
  const usersQuery = useGetApiUsers()

  const usersEmailById = useMemo(
    () =>
      new Map(
        (usersQuery.data?.body ?? []).map((user: { id: string; email?: string | null }) => [
          user.id,
          user.email ?? user.id,
        ]),
      ),
    [usersQuery.data],
  )

  const operationsQuery = useQuery({
    queryKey: ['fileCopy', 'operations', statuses, refreshKey],
    queryFn: () =>
      FileCopyService.listOperations({ statuses: statuses.length ? statuses : undefined }),
    refetchInterval: data => {
      const hasActive = (data?.operations ?? []).some(
        op => op.status === 'pending' || op.status === 'running',
      )
      return hasActive ? 1000 : false
    },
  })

  const cancelMutation = useMutation({
    mutationFn: (operationId: string) => FileCopyService.cancelOperation(operationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fileCopy', 'operations'] })
    },
  })

  const operations = useMemo(() => operationsQuery.data?.operations ?? [], [operationsQuery.data])
  const hasActive = useMemo(
    () => operations.some(op => op.status === 'pending' || op.status === 'running'),
    [operations],
  )

  useEffect(() => {
    if (refreshKey > 0) {
      queryClient.invalidateQueries({ queryKey: ['fileCopy', 'operations'] })
    }
  }, [refreshKey, queryClient])

  const items: FileCopyOperationRow[] = operations.map((operation: FileCopyOperation) => {
    const progress =
      operation.bytesTotal > 0
        ? Math.round((operation.bytesDone / operation.bytesTotal) * 100)
        : operation.filesCount > 0
          ? Math.round(
              ((operation.successCount + operation.failureCount + operation.cancelledCount) /
                operation.filesCount) *
                100,
            )
          : 0

    return {
      id: operation.id,
      operation: (
        <Stack gap={0}>
          <Text size="sm" fw={500}>
            {operation.id}
          </Text>
          <Text size="xs" c="dimmed">
            {operation.createdAt ? DateHelper.format(operation.createdAt, LONG_FORMAT) : '—'}
          </Text>
        </Stack>
      ),
      createdBy: (
        <Text size="sm">
          {usersEmailById.get(operation.createdByUserId ?? '') ?? operation.createdByUserId ?? '—'}
        </Text>
      ),
      status: operation.status,
      statusItem: (
        <Badge color={STATUS_COLORS[operation.status]} variant="light">
          {t(`pages.fileCopy.statuses.${operation.status}`)}
        </Badge>
      ),
      selectionAndDestination: (
        <Stack gap={2}>
          <Text size="sm">
            {t('pages.fileCopy.list.selectionSummary', {
              folders: operation.config.selection.folderIds.length,
              files: operation.config.selection.fileIds.length,
            })}
          </Text>
          <Text size="xs" c="dimmed">
            gs://{operation.config.destination.bucketName}
            {operation.config.destination.subFolderPrefix}
          </Text>
        </Stack>
      ),
      progress: (
        <Stack gap={4} style={{ minWidth: 160 }}>
          <Progress
            value={progress}
            size="sm"
            animated={operation.status === 'running'}
            color={progress === 100 ? 'green' : undefined}
          />
          <Text size="xs" c="dimmed">
            {t('pages.fileCopy.detail.progressSummary', {
              progress,
              done: NumberFormatter.formatDigitalByte(operation.bytesDone),
              total: NumberFormatter.formatDigitalByte(operation.bytesTotal),
            })}
          </Text>
        </Stack>
      ),
      files: (
        <Stack gap={0}>
          <Text size="sm">
            {operation.successCount} / {operation.filesCount}
          </Text>
          {operation.failureCount > 0 && (
            <Text size="xs" c="red">
              {t('pages.fileCopy.list.failures', { count: operation.failureCount })}
            </Text>
          )}
        </Stack>
      ),
    }
  })

  const columns: LXTableColumn<FileCopyOperationRow>[] = [
    { key: 'operation', value: t('pages.fileCopy.list.headers.operation'), type: 'component' },
    { key: 'statusItem', value: t('pages.fileCopy.list.headers.status'), type: 'component' },
    {
      key: 'selectionAndDestination',
      value: t('pages.fileCopy.list.headers.selectionAndDestination'),
      type: 'component',
    },
    { key: 'progress', value: t('pages.fileCopy.list.headers.progress'), type: 'component' },
    { key: 'files', value: t('pages.fileCopy.list.headers.files'), type: 'component' },
    { key: 'createdBy', value: t('pages.fileCopy.list.headers.createdBy'), type: 'component' },
  ]

  const getActions = (row: FileCopyOperationRow): JSX.Element[] => {
    if (row.status !== 'pending' && row.status !== 'running') {
      return []
    }

    return [
      <Button
        key={`cancel-${row.id}`}
        size="xs"
        color="red"
        variant="subtle"
        loading={cancelMutation.isLoading && cancelMutation.variables === row.id}
        onClick={event => {
          event.stopPropagation()
          cancelMutation.mutate(row.id)
        }}
      >
        {t('pages.fileCopy.list.cancel')}
      </Button>,
    ]
  }

  return (
    <Stack gap="xs">
      <Group
        justify="space-between"
        align="flex-start
      "
      >
        <Title order={4}>{t('pages.fileCopy.fileCopyOperationsList')}</Title>
        <Stack gap="xs">
          <Button size="compact-sm" onClick={onGoToCreate} variant="light">
            {t('pages.fileCopy.list.newCopy')}
          </Button>
          <Button
            size="compact-xs"
            variant={
              statuses.includes('running') || statuses.includes('pending') ? 'filled' : 'subtle'
            }
            onClick={() => setStatuses(prev => (prev.length ? [] : ['pending', 'running']))}
          >
            {hasActive || statuses.length
              ? t('pages.fileCopy.list.filterAll')
              : t('pages.fileCopy.list.filterActive')}
          </Button>
        </Stack>
      </Group>

      {cancelMutation.isError && (
        <Alert color="red" title={t('pages.fileCopy.create.error')}>
          {t('pages.fileCopy.list.cancelError', { error: String(cancelMutation.error) })}
        </Alert>
      )}

      {operationsQuery.isLoading ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : operations.length === 0 ? (
        <Stack align="center" py="xl" gap="sm">
          <Text c="dimmed">{t('pages.fileCopy.list.empty')}</Text>
          <Button onClick={onGoToCreate} variant="light">
            {t('pages.fileCopy.list.emptyCta')}
          </Button>
        </Stack>
      ) : (
        <LXTable
          items={items}
          columns={columns}
          onRowClick={row => navigate(`/file-copy/${row.id}`)}
          setActions={getActions}
          verticalSpacing="sm"
        />
      )}
    </Stack>
  )
}
